import React from 'react'
import Field from 'components/field'
import Select from 'components/select'
import { NUMBER_COLUMN_TYPES, DATE_COLUMN_TYPES } from 'components/spreadsheet/constants/const'
import { IColumnTypes } from 'components/spreadsheet/types'

const DECIMAL_COLUMN_TYPES: IColumnTypes[] = ['float', 'percentage']

// The formatting settings shared by the column configuration UIs. Field names/types mirror the column
// model so a consumer can pass its draft/column straight through as `value` and route `onChange` patches
// to wherever it persists them.
export interface ColumnFormattingValue {
  thousandSeparator: boolean
  decimalPlaces: number
  dateFormat: string | null
  displayLink: boolean
}

interface ColumnTypeFormattingFieldsProps {
  kind: IColumnTypes
  value: ColumnFormattingValue
  onChange: (patch: Partial<ColumnFormattingValue>) => void
}

const DATE_FORMATS = ['DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD', 'D MMM YYYY', 'DD MMM YYYY']
const DATETIME_FORMATS = ['DD/MM/YYYY HH:mm', 'MM/DD/YYYY HH:mm', 'YYYY-MM-DD HH:mm:ss', 'D MMM YYYY HH:mm']
const DECIMAL_PLACES = Array.from({ length: 11 }, (_, places) => ({ value: String(places), label: String(places) }))
const EXPORT_WIDTHS = [3, 4, 5, 6, 8, 10, 12]

// What a date column with no format of its own shows (see the date cell), named for it in the list.
const UNSET_DATE_FORMAT = 'YYYY-MM-DD'
const UNSET_DATETIME_FORMAT = 'YYYY-MM-DD HH:mm:ss'

// A choice list that keeps a value set some other way (a date format of the column's own, typed in the list).
const withCurrent = (
  options: string[],
  current: string | null,
  unset: string
): Array<{ value: string; label: string }> => [
  { value: '', label: `${unset} (default)` },
  ...[...options, ...(current && !options.includes(current) ? [current] : [])].map((format) => ({
    value: format,
    label: format
  }))
]

// The formatting settings a column's type needs (number, decimal, date and link formats), each a labelled
// dropdown, as the column's Type above them is. Which show is derived from the column kind, matching the
// model's own rules; types with no such settings render nothing.
export const ColumnTypeFormattingFields: React.FC<ColumnTypeFormattingFieldsProps> = ({ kind, value, onChange }) => {
  const isNumber = NUMBER_COLUMN_TYPES.includes(kind)
  const isDecimal = DECIMAL_COLUMN_TYPES.includes(kind)
  const isDate = DATE_COLUMN_TYPES.includes(kind)
  if (!isNumber && !isDecimal && !isDate && kind !== 'link') return null

  const field = (
    id: string,
    label: string,
    options: Array<{ value: string; label: string }>,
    selected: string,
    onPick: (option: string) => void,
    className?: string,
    hint?: string,
    // A value of one's own, typed in the list's search and added from it.
    custom?: { onCreate: (value: string) => void; searchPlaceholder: string }
  ) => (
    <Field label={label} htmlFor={id} className={className}>
      <Select
        inputId={id}
        size="sm"
        options={options}
        optionsSelected={[selected]}
        onOptionClick={onPick}
        onCreateOption={custom?.onCreate}
        searchPlaceholder={custom?.searchPlaceholder}
      />
      {hint && <span className="text-[12px] text-muted">{hint}</span>}
    </Field>
  )

  // Number format and decimal places side by side when a column has both: two short choices, half a row each.
  const paired = isNumber && isDecimal
  const half = paired ? 'flex min-w-0 flex-col gap-1.5' : undefined
  const numberFormat =
    isNumber &&
    field(
      'column-number-format',
      'Number format',
      [
        { value: 'Comma', label: '1,000' },
        { value: 'None', label: '1000' }
      ],
      value.thousandSeparator ? 'Comma' : 'None',
      (option) => onChange({ thousandSeparator: option !== 'None' }),
      half
    )
  const decimalPlaces =
    isDecimal &&
    field(
      'column-decimal-places',
      'Decimal places',
      DECIMAL_PLACES,
      String(value.decimalPlaces),
      (places) => onChange({ decimalPlaces: parseInt(places, 10) }),
      half
    )

  return (
    <>
      {paired ? (
        <div className="grid grid-cols-2 gap-2 px-3 pt-3">
          {numberFormat}
          {decimalPlaces}
        </div>
      ) : (
        <>
          {numberFormat}
          {decimalPlaces}
        </>
      )}
      {isDate &&
        field(
          'column-date-format',
          'Date format',
          kind === 'datetime'
            ? withCurrent(DATETIME_FORMATS, value.dateFormat, UNSET_DATETIME_FORMAT)
            : withCurrent(DATE_FORMATS, value.dateFormat, UNSET_DATE_FORMAT),
          value.dateFormat ?? '',
          (format) => onChange({ dateFormat: format || null }),
          undefined,
          "Dates show in each person's own time zone.",
          { onCreate: (format) => onChange({ dateFormat: format }), searchPlaceholder: 'Search or type a format' }
        )}
      {kind === 'link' &&
        field(
          'column-link-display',
          'Link shows as',
          [
            { value: 'text', label: 'Full link' },
            { value: 'icon', label: 'Link icon' }
          ],
          value.displayLink ? 'text' : 'icon',
          (option) => onChange({ displayLink: option === 'text' })
        )}
    </>
  )
}

// The widths a column can be given in exports, in cm, keeping one set some other way.
export const exportWidths = (current: number | null): number[] =>
  [...EXPORT_WIDTHS, ...(current !== null && !EXPORT_WIDTHS.includes(current) ? [current] : [])].sort((a, b) => a - b)
